/**
 * Trade Calculation Service
 *
 * Provides utilities for calculating trade diffs between a user's current positions
 * and a target portfolio allocation. Used in the User Portfolio Diff View within
 * Rebalance Events.
 *
 * Key Calculations:
 * - Current Weight %: (position market value / net liquidation) * 100
 * - Target Weight %: From the target portfolio's contract weightage
 * - Target Quantity: floor((netLiq * targetWt%) / priceUSD), or lot-rounded when minSize > 0
 * - Trade Quantity:
 *     No lot constraint: targetQty - currentQty
 *     Lot-constrained:   round((rawTargetQty - currentQty) / minSize) * minSize
 *   (positive = BUY, negative = SELL)
 * - Effective Target Qty: currentQty + tradeQty (where customer ends up after trade)
 * - Trade Value: tradeQty * price (in USD)
 */

import { IbkrAccountSummary, IbkrPositionV2 } from "../types";
import { RebalanceEventContract } from "../types/rebalanceContract";

// ============================================================================
// Types
// ============================================================================

/**
 * Position with calculated diff information
 */
export interface PositionWithDiff extends IbkrPositionV2 {
  /** Target weight from the portfolio (0-100) */
  targetWeightPct: number | null;
  /** Current weight based on mktValue / netLiq (0-100), null if FX rate unavailable */
  currentWeightPct: number | null;
  /** Target quantity of shares to hold, null if FX rate unavailable */
  targetQty: number | null;
  /** Raw target quantity before lot size rounding, null if FX rate unavailable */
  rawTargetQty: number | null;
  /** Trade quantity: positive = BUY, negative = SELL, null if not in target or FX unavailable */
  tradeQty: number | null;
  /** Trade value in position's currency, null if FX rate unavailable */
  tradeValue: number | null;
  /** Trade value converted to USD, null if FX rate unavailable */
  tradeValueUsd: number | null;
  /** Whether this position is in the target portfolio */
  isInTarget: boolean;
  /** Projected position after lot-rounded trade executes (currentQty + tradeQty) */
  effectiveTargetQty: number | null;
  /** Symbol from the ticker data lookup (if available) */
  symbol?: string;
  /** Minimum trading lot size (0 = no restriction) */
  minSize: number | null;
}

/**
 * Synthetic position for contracts in target but not held by user
 */
export interface SyntheticPosition {
  conId: number;
  symbol: string;
  currency: string;
  description: string | null;
  secType: string; // Security type from target contract (e.g., "STK")
  position: number; // 0 since user doesn't hold it
  mktPrice: number;
  mktValue: number; // 0
  avgCost: number;
  unrealizedPnl: number;
  realizedPnl: number;
  targetWeightPct: number;
  currentWeightPct: number; // 0
  targetQty: number | null; // null if FX rate unavailable
  rawTargetQty: number | null; // Raw target quantity before lot size rounding
  tradeQty: number | null; // null if FX rate unavailable
  tradeValue: number | null; // null if FX rate unavailable
  tradeValueUsd: number | null; // null if FX rate unavailable
  effectiveTargetQty: number | null; // Projected position after lot-rounded trade
  isInTarget: boolean; // true
  isSynthetic: boolean; // true - marks this as a synthetic row
  minSize: number | null; // Minimum trading lot size (0 = no restriction)
}

/**
 * FX rate lookup (currency -> USD rate)
 */
export type FxRates = Record<string, number>;

/**
 * Price data lookup (conId -> price in local currency)
 */
export type PriceData = Record<number, { price: number; currency: string }>;

// ============================================================================
// FX Rate Helpers
// ============================================================================

/**
 * Get exchange rate from a currency to USD.
 * Returns 1.0 for USD, uses provided rates from fxRates parameter.
 * Live rates are fetched via useFxRates hook and passed here.
 * Returns null if rate is not available - no fallback values.
 *
 * @param currency - The source currency code (e.g., "EUR", "GBP")
 * @param fxRates - FX rates map from useFxRates hook (currency -> USD rate)
 * @returns Exchange rate to convert 1 unit of currency to USD, or null if unavailable
 */
export function getExchangeRateToUsd(currency: string | null | undefined, fxRates?: FxRates): number | null {
  if (!currency) return 1.0;

  const normalizedCurrency = currency.toUpperCase();

  // USD is always 1.0
  if (normalizedCurrency === "USD") {
    return 1.0;
  }

  // fxRates must be provided for non-USD currencies
  if (!fxRates) {
    return null;
  }

  const rate = fxRates[normalizedCurrency];
  if (rate === undefined) {
    return null;
  }

  return rate;
}

/**
 * Convert a value from one currency to USD.
 * Returns null if FX rate is unavailable.
 *
 * @param value - The monetary value to convert
 * @param currency - The source currency code
 * @param customRates - Optional custom FX rates
 * @returns The value in USD, or null if FX rate unavailable
 */
export function convertToUsd(value: number, currency: string | null | undefined, customRates?: FxRates): number | null {
  const rate = getExchangeRateToUsd(currency, customRates);
  if (rate === null) return null;
  return value * rate;
}

// ============================================================================
// Trade Calculation Functions
// ============================================================================

/**
 * Calculate the current weight percentage for a position.
 * Returns null if FX rate is unavailable.
 *
 * @param mktValue - The market value of the position (in any currency)
 * @param netLiq - The net liquidation value of the account (in USD)
 * @param currency - The currency of the position
 * @param fxRates - Optional FX rates for conversion
 * @returns The weight as a percentage (0-100), or null if FX rate unavailable
 */
export function calculateCurrentWeight(
  mktValue: number,
  netLiq: number,
  currency: string | null | undefined,
  fxRates?: FxRates
): number | null {
  if (netLiq <= 0) return 0;

  // Convert market value to USD for consistent comparison
  const mktValueUsd = convertToUsd(mktValue, currency, fxRates);
  if (mktValueUsd === null) return null;

  return (mktValueUsd / netLiq) * 100;
}

/**
 * Result of target quantity calculation
 */
export interface TargetQuantityResult {
  /** Rounded target quantity (after lot size rounding) */
  targetQty: number;
  /** Raw target quantity (before lot size rounding) */
  rawTargetQty: number;
}

/**
 * Calculate the target quantity of shares to hold based on target weight.
 * Returns null if priceUsd is unavailable (FX rate fetch failed).
 *
 * @param netLiq - Net liquidation value in USD
 * @param targetWeightPct - Target weight as percentage (0-100)
 * @param priceUsd - Price per share in USD, or null if unavailable
 * @param minSize - Minimum lot size (0 = no restriction, default 0)
 * @returns Object with targetQty (rounded) and rawTargetQty (unrounded), or null if cannot calculate
 */
export function calculateTargetQuantity(
  netLiq: number,
  targetWeightPct: number,
  priceUsd: number | null,
  minSize: number = 0
): TargetQuantityResult | null {
  if (priceUsd === null) return null;
  if (priceUsd <= 0 || netLiq <= 0) return { targetQty: 0, rawTargetQty: 0 };

  const targetValue = netLiq * (targetWeightPct / 100);
  const rawQty = targetValue / priceUsd;

  // If no lot size restriction (0 or null), floor to whole shares
  if (!minSize || minSize <= 0) {
    return { targetQty: Math.floor(rawQty), rawTargetQty: rawQty };
  }

  // Round to nearest lot size (supports fractions like 0.5, 0.1)
  return {
    targetQty: Math.round(rawQty / minSize) * minSize,
    rawTargetQty: rawQty,
  };
}

/**
 * Contract details map type for minSize lookup
 */
export type ContractDetailsMap = Record<number, { minSize?: number }>;

/**
 * Calculate diffs for all positions against a target portfolio.
 *
 * This function:
 * 1. Enriches each position with diff calculations
 * 2. Adds synthetic positions for target contracts not held by user
 * 3. Respects user exclusions (contracts excluded won't show trade signals)
 *
 * @param positions - User's current positions (from V2 API)
 * @param accountSummary - User's account summary (for netLiq)
 * @param targetContracts - Target portfolio contracts with weights
 * @param tickerData - Lookup map of conId -> symbol
 * @param excludedConIds - Set of contract IDs the user has excluded
 * @param fxRates - Optional FX rates for currency conversion
 * @param priceOverrides - Optional price overrides (if we have fresher prices)
 * @param fullRefresh - If true, positions not in target are marked for SELL; if false, only target contracts get trades
 * @param contractDetailsMap - Optional contract details for minSize lookup
 * @returns Array of positions with diff calculations
 */
export function calculateDiffsForPositions(
  positions: IbkrPositionV2[],
  accountSummary: IbkrAccountSummary | null,
  targetContracts: RebalanceEventContract[],
  tickerData: Record<number, string>,
  excludedConIds: Set<number> = new Set(),
  fxRates?: FxRates,
  priceOverrides?: PriceData,
  fullRefresh: boolean = true,
  contractDetailsMap?: ContractDetailsMap
): (PositionWithDiff | SyntheticPosition)[] {
  const netLiq = Number(accountSummary?.netLiquidationSecurity) || 0;

  // Build lookup map for target contracts: conId -> contract info
  const targetMap = new Map<number, RebalanceEventContract>();
  targetContracts.forEach(contract => {
    if (contract.conid !== undefined) {
      targetMap.set(contract.conid, contract);
    }
  });

  // Track which target contracts we've seen in user's positions
  const seenTargetConIds = new Set<number>();

  // Process existing positions
  const enrichedPositions: PositionWithDiff[] = positions.map(pos => {
    const conId = pos.conId;
    const targetContract = targetMap.get(conId);
    const isInTarget = targetContract !== undefined;
    const isExcluded = excludedConIds.has(conId);

    if (isInTarget) {
      seenTargetConIds.add(conId);
    }

    // Get price - use override if available, otherwise use position's mktPrice
    const price = priceOverrides?.[conId]?.price ?? (Number(pos.mktPrice) || 0);
    const currency = priceOverrides?.[conId]?.currency ?? pos.currency;
    const priceUsd = convertToUsd(price, currency, fxRates);
    const positionQty = Number(pos.position) || 0;

    // Get minSize from contract details (default 0 = no restriction)
    const minSize = contractDetailsMap?.[conId]?.minSize ?? 0;

    // Market value should reflect shares * price
    const effectiveMktValue = positionQty * price;

    // Calculate current weight
    const currentWeightPct = calculateCurrentWeight(effectiveMktValue, netLiq, currency, fxRates);

    // Calculate target values (null if not in target or excluded)
    let targetWeightPct: number | null = null;
    let targetQty: number | null = null;
    let rawTargetQty: number | null = null;
    let tradeQty: number | null = null;
    let tradeValue: number | null = null;
    let tradeValueUsd: number | null = null;
    let effectiveTargetQty: number | null = null;

    if (isInTarget && !isExcluded && targetContract) {
      targetWeightPct = targetContract.weightage;
      const qtyResult = calculateTargetQuantity(netLiq, targetWeightPct, priceUsd, minSize);
      targetQty = qtyResult?.targetQty ?? null;
      rawTargetQty = qtyResult?.rawTargetQty ?? null;

      const currentQty = Number(pos.position) || 0;

      if (rawTargetQty !== null) {
        if (minSize > 0) {
          // Lot-constrained: round the TRADE to lot multiples, not the target.
          // The lot-size constraint applies to order submission, not position size.
          const rawTradeQty = rawTargetQty - currentQty;
          // `+ 0` normalizes JS -0 to 0 without masking NaN from bad upstream data
          tradeQty = Math.round(rawTradeQty / minSize) * minSize + 0;
          effectiveTargetQty = currentQty + tradeQty;
        } else {
          // No lot constraint: floor target to whole shares (existing behavior, unchanged)
          tradeQty = targetQty !== null ? targetQty - currentQty : null;
          effectiveTargetQty = targetQty;
        }
      }

      // Trade value in local currency
      tradeValue = tradeQty !== null ? tradeQty * price : null;
      // Trade value in USD (null if priceUsd unavailable)
      tradeValueUsd = tradeQty !== null && priceUsd !== null ? tradeQty * priceUsd : null;
    } else if (!isInTarget && fullRefresh) {
      // fullRefresh mode: Not in target -> sell existing position
      targetWeightPct = 0;
      targetQty = 0;
      rawTargetQty = 0;

      const currentQty = positionQty;
      tradeQty = currentQty !== 0 ? -currentQty : 0;
      effectiveTargetQty = 0;

      tradeValue = tradeQty * price;
      // Trade value in USD (null if priceUsd unavailable)
      tradeValueUsd = priceUsd !== null ? tradeQty * priceUsd : null;
    }
    // When !fullRefresh and !isInTarget: all diff fields remain null (no trade signal)

    return {
      ...pos,
      mktValue: effectiveMktValue,
      targetWeightPct,
      currentWeightPct,
      targetQty,
      rawTargetQty,
      tradeQty,
      tradeValue,
      tradeValueUsd,
      effectiveTargetQty,
      isInTarget,
      symbol: tickerData[conId] || pos.description || undefined,
      minSize: minSize || null,
    };
  });

  // Add synthetic positions for target contracts not held by user
  const syntheticPositions: SyntheticPosition[] = [];

  targetContracts.forEach(contract => {
    if (contract.conid === undefined) return;

    const conId = contract.conid;
    const isExcluded = excludedConIds.has(conId);

    // Skip if user already holds this or it's excluded
    if (seenTargetConIds.has(conId) || isExcluded) return;

    // For synthetic positions, we need price data
    // If we don't have it, we can't calculate trade quantities accurately
    const priceInfo = priceOverrides?.[conId];
    const price = priceInfo?.price || 0;
    const currency = priceInfo?.currency || "USD";
    const priceUsd = convertToUsd(price, currency, fxRates);

    // Get minSize from contract details (default 0 = no restriction)
    const minSize = contractDetailsMap?.[conId]?.minSize ?? 0;

    const targetWeightPct = contract.weightage;
    // Calculate target quantities - null if price unavailable (can't calculate without price)
    // This ensures contracts without price data still appear in the view with null values
    const qtyResult =
      price > 0
        ? calculateTargetQuantity(netLiq, targetWeightPct, priceUsd, minSize)
        : null; // No price = can't calculate quantities
    const targetQty = qtyResult?.targetQty ?? null;
    const rawTargetQty = qtyResult?.rawTargetQty ?? null;
    const tradeQty = targetQty; // Since current position is 0, tradeQty = targetQty (null if no price)
    const tradeValue = tradeQty !== null && price > 0 ? tradeQty * price : null;
    // tradeValueUsd is null if priceUsd unavailable
    const tradeValueUsd = tradeQty !== null && priceUsd !== null ? tradeQty * priceUsd : null;

    syntheticPositions.push({
      conId,
      symbol: tickerData[conId] || `ConId: ${conId}`,
      currency,
      description: tickerData[conId] || null,
      secType: contract.type,
      position: 0,
      mktPrice: price,
      mktValue: 0,
      avgCost: 0,
      unrealizedPnl: 0,
      realizedPnl: 0,
      targetWeightPct,
      currentWeightPct: 0,
      targetQty,
      rawTargetQty,
      tradeQty,
      tradeValue,
      tradeValueUsd,
      effectiveTargetQty: targetQty, // Synthetic: current=0, so effective = target
      isInTarget: true,
      isSynthetic: true,
      minSize: minSize || null,
    });
  });

  // Combine and return all positions
  return [...enrichedPositions, ...syntheticPositions];
}

/**
 * Filter positions to show only those relevant to the rebalance event.
 * This filters to positions that are either:
 * 1. In the target portfolio, OR
 * 2. Have non-zero trades to execute
 *
 * @param positions - All positions with diff calculations
 * @param showAllPositions - If true, show all positions regardless of target
 * @returns Filtered array of positions
 */
export function filterRelevantPositions(
  positions: (PositionWithDiff | SyntheticPosition)[],
  showAllPositions: boolean = false
): (PositionWithDiff | SyntheticPosition)[] {
  if (showAllPositions) {
    return positions;
  }

  return positions.filter(pos => {
    // Always show if in target portfolio
    if (pos.isInTarget) return true;

    // Show positions not in target only if they have significant value
    // (user might need to sell these to rebalance)
    const mktValue = Math.abs(Number(pos.mktValue) || 0);
    return mktValue > 0;
  });
}

/**
 * Calculate summary statistics for the trade plan.
 *
 * @param positions - Positions with diff calculations
 * @returns Summary statistics
 */
export function calculateTradeSummary(positions: (PositionWithDiff | SyntheticPosition)[]): {
  totalBuyValue: number;
  totalSellValue: number;
  netTradeValue: number;
  buyCount: number;
  sellCount: number;
  holdCount: number;
} {
  let totalBuyValue = 0;
  let totalSellValue = 0;
  let buyCount = 0;
  let sellCount = 0;
  let holdCount = 0;

  positions.forEach(pos => {
    const tradeValue = pos.tradeValueUsd ?? 0;
    const tradeQty = pos.tradeQty ?? 0;

    if (tradeQty > 0) {
      totalBuyValue += tradeValue;
      buyCount++;
    } else if (tradeQty < 0) {
      totalSellValue += Math.abs(tradeValue);
      sellCount++;
    } else if (pos.isInTarget) {
      holdCount++;
    }
  });

  return {
    totalBuyValue,
    totalSellValue,
    netTradeValue: totalBuyValue - totalSellValue,
    buyCount,
    sellCount,
    holdCount,
  };
}

// ============================================================================
// Enrichment Helpers
// ============================================================================

/**
 * Recalculate effectiveTargetQty when tradeQty is overridden after initial
 * service computation (e.g., stage-assigned diff or local manual edit).
 *
 * @param currentQty - Customer's current holding (row.shares)
 * @param overrideTradeQty - The overriding tradeQty value (may be null)
 * @param fallback - Original effectiveTargetQty to preserve when override is null
 * @returns Updated effectiveTargetQty
 */
export function recalcEffectiveTargetQty(
  currentQty: number,
  overrideTradeQty: number | string | null,
  fallback: number | null
): number | null {
  if (overrideTradeQty == null) return fallback;
  const normalizedTradeQty = typeof overrideTradeQty === "number" ? overrideTradeQty : Number(overrideTradeQty);
  return Number.isFinite(normalizedTradeQty) ? currentQty + normalizedTradeQty : fallback;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if a position is synthetic (not actually held by user)
 */
export function isSyntheticPosition(pos: PositionWithDiff | SyntheticPosition): pos is SyntheticPosition {
  return "isSynthetic" in pos && pos.isSynthetic === true;
}

// ============================================================================
// Export
// ============================================================================

const tradeCalculationService = {
  getExchangeRateToUsd,
  convertToUsd,
  calculateCurrentWeight,
  calculateTargetQuantity,
  calculateDiffsForPositions,
  filterRelevantPositions,
  calculateTradeSummary,
  recalcEffectiveTargetQty,
  isSyntheticPosition,
};

export default tradeCalculationService;
